import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import withStyles from 'isomorphic-style-loader/withStyles';
import cx from 'classnames';

import OptionRow from './OptionRow';
import OptionEditModal from './OptionEditModal';
import messages from '../../locale/jsonMessages';
import { DEFAULT_OPTION_SETTINGS } from '../../helpers/bookingOptionsBilling';
import {
  fetchListingOptions, saveListingOption, setListingOptionActive,
  deleteListingOption, adminSetListingOptionDisabled
} from '../../actions/Listing/listingOptions';
import { userRestriction } from '../../helpers/DemoRestriction/demoRestriction';
import showToaster from '../../helpers/toasterMessages/showToaster';
import s from './ListingOptionsManager.css';

// "Options et services" block: the fixed services enabled by the admin + the custom options (limit set by the admin), saved immediately through the
// option mutations (it does not use the ListPlaceStep3 redux form). Also used by the admin with adminMode.
export function ListingOptionsManager({
  listId, currency, adminMode: adminModeProp = false, isAdmin = false, intl, dispatch, scrollToOnLoad = false, initialResult = null,
}) {
  const { formatMessage } = intl;
  // An administrator editing a vehicle also gets the "Disable by GOZY" control.
  const adminMode = adminModeProp || Boolean(isAdmin);
  // initialResult (same shape as the server answer) skips the first fetch: used for server rendering and tests.
  const [options, setOptions] = useState(initialResult ? initialResult.results || [] : []);
  const [loading, setLoading] = useState(!initialResult);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [modalError, setModalError] = useState(null);
  const [reasonFor, setReasonFor] = useState(null);
  const [reason, setReason] = useState('');
  // The limit comes from the server (admin setting); the default only applies until the first answer.
  const [maxCustomFromServer, setMaxCustomFromServer] = useState(initialResult && Number.isInteger(initialResult.maxCustom) ? initialResult.maxCustom : null);

  const maxCustom = maxCustomFromServer === null ? DEFAULT_OPTION_SETTINGS.maxCustomOptions : maxCustomFromServer;
  const customOptions = options.filter((option) => !option.isFixed);
  const fixedOptions = options.filter((option) => option.isFixed);
  const realRows = options.filter((option) => option.id);
  const remaining = Math.max(0, maxCustom - customOptions.length);
  // An owner can be above a lowered limit: existing options stay editable/deletable, adding is blocked.
  const overLimit = customOptions.length > maxCustom;

  const apply = useCallback((result) => {
    if (result && result.status === 'success') {
      setOptions(result.results || []);
      if (Number.isInteger(result.maxCustom)) setMaxCustomFromServer(result.maxCustom);
      setError(null);
      return true;
    }
    setError((result && result.errorMessage) || formatMessage(messages.somethingWentWrong));
    return false;
  }, [formatMessage]);

  useEffect(() => {
    if (initialResult) return undefined;
    let active = true;
    (async () => {
      setLoading(true);
      const result = await dispatch(fetchListingOptions(listId));
      if (active) {
        apply(result);
        setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [listId]);

  useEffect(() => {
    if (scrollToOnLoad && !loading && typeof window !== 'undefined' && window.location.hash === '#booking-options') {
      const node = document.getElementById('booking-options');
      if (node && node.scrollIntoView) node.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [loading]);

  // Demo accounts cannot change a listing (same guard as the other owner forms).
  const guard = async () => {
    if (adminMode) return true;
    const restricted = await dispatch(userRestriction({ restrictType: 'listing', restrictValue: listId }));
    if (restricted) {
      showToaster({ messageId: 'demoWarning', toasterType: 'warning' });
      return false;
    }
    return true;
  };

  const run = async (action) => {
    if (busy || !(await guard())) return false;
    setBusy(true);
    const result = await dispatch(action);
    const ok = apply(result);
    setBusy(false);
    return ok;
  };

  const onToggle = async (option, checked) => {
    // A service without price cannot be activated: ask for the price first.
    if (checked && !(Number(option.price) > 0)) {
      setModalError(null);
      setEditing({ ...option, activate: true });
      return;
    }
    await run(setListingOptionActive({ listId, slot: option.slot, isActive: checked }));
  };

  const onDelete = (option) => run(deleteListingOption({ listId, slot: option.slot }));

  const onSave = async (input) => {
    if (busy || !(await guard())) return;
    setBusy(true);
    const result = await dispatch(saveListingOption({ listId, ...input }));
    setBusy(false);
    if (result && result.status === 'success') {
      apply(result);
      setEditing(null);
      setModalError(null);
    } else {
      setModalError((result && result.errorMessage) || formatMessage(messages.somethingWentWrong));
    }
  };

  const onAdminToggle = (option) => {
    if (option.adminDisabled) {
      run(adminSetListingOptionDisabled({ listId, slot: option.slot, disabled: false, reason: null }));
    } else {
      setReason('');
      setReasonFor(option);
    }
  };

  const confirmAdminDisable = async () => {
    const option = reasonFor;
    setReasonFor(null);
    await run(adminSetListingOptionDisabled({ listId, slot: option.slot, disabled: true, reason }));
  };

  const rowProps = { currency, busy, adminMode, styles: s, onToggle, onDelete, onAdminToggle,
    onEdit: (option) => { setModalError(null); setEditing(option); } };

  return (
    <section id="booking-options" className={s.manager} aria-busy={loading}>
      <header className={s.header}>
        <h3 className={s.title}>{formatMessage(messages.boTitle)}</h3>
        <p className={s.subtitle}>{formatMessage(messages.boOwnerSubtitle)}</p>
      </header>

      {error && <div className={s.errorBanner} role="alert">{error}</div>}
      {loading && <p className={s.muted}>{formatMessage(messages.boLoading)}</p>}

      {!loading && (
        <>
          <div className={s.group}>
            <h4 className={s.groupTitle}>{formatMessage(messages.boFixedServices)}</h4>
            <ul className={s.list}>
              {fixedOptions.map((option) => <OptionRow key={option.slot} option={option} {...rowProps} />)}
            </ul>
          </div>

          <div className={s.group}>
            <h4 className={s.groupTitle}>
              {formatMessage(messages.boCustomOptions, { max: maxCustom })}
              <span className={s.counter} aria-live="polite">
                {formatMessage(messages.boCountAdded, { count: customOptions.length, max: maxCustom })}
              </span>
            </h4>
            {maxCustom === 0 && customOptions.length === 0 && <p className={s.muted}>{formatMessage(messages.boNoCustomAllowed)}</p>}
            {maxCustom > 0 && customOptions.length === 0 && <p className={s.muted}>{formatMessage(messages.boEmptyCustom)}</p>}
            <ul className={s.list}>
              {customOptions.map((option) => <OptionRow key={option.slot} option={option} {...rowProps} />)}
            </ul>
            {overLimit && <p className={s.limitNote}>{formatMessage(messages.boOverLimit, { max: maxCustom })}</p>}
            {remaining > 0 && (
              <button type="button" className={s.addButton} disabled={busy}
                onClick={() => { setModalError(null); setEditing({ slot: null, isFixed: false, price: 0 }); }}>
                <span aria-hidden="true">+</span> {formatMessage(messages.boAddOption)}
                <small>{formatMessage(messages.boRemaining, { count: remaining })}</small>
              </button>
            )}
            {remaining === 0 && maxCustom > 0 && !overLimit && (
              <p className={s.limitNote}>{formatMessage(messages.boLimitReached, { max: maxCustom })}</p>
            )}
          </div>
        </>
      )}

      {editing && (
        <OptionEditModal
          key={editing.slot || 'new'}
          option={editing}
          rows={realRows}
          maxCustom={maxCustom}
          currency={currency}
          styles={s}
          busy={busy}
          serverError={modalError}
          onSave={onSave}
          onClose={() => { setEditing(null); setModalError(null); }}
        />
      )}

      {reasonFor && (
        <div className={s.reasonBox} role="dialog" aria-label={formatMessage(messages.boDisableByGozy)}>
          <label htmlFor="bo-admin-reason">{formatMessage(messages.boDisableReason)}</label>
          <input id="bo-admin-reason" type="text" className="form-control" maxLength={255} value={reason}
            onChange={(e) => setReason(e.target.value)} />
          <div className={s.reasonActions}>
            <button type="button" className={s.dangerButton} onClick={confirmAdminDisable}>
              {formatMessage(messages.boDisableByGozy)}
            </button>
            <button type="button" className={s.textButton} onClick={() => setReasonFor(null)}>
              {formatMessage(messages.cancel)}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

ListingOptionsManager.propTypes = {
  listId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  currency: PropTypes.string,
  adminMode: PropTypes.bool,
  scrollToOnLoad: PropTypes.bool,
  initialResult: PropTypes.shape({ results: PropTypes.array, maxCustom: PropTypes.number }),
};

const mapState = (state) => ({
  isAdmin: Boolean(state?.runtime?.isAdminAuthenticated),
});

export default injectIntl(withStyles(s)(connect(mapState)(ListingOptionsManager)));
